// frontend/src/App.jsx
import SuperAdminApp from './pages/superadmin/SuperAdminApp';
import GiftCardCheck from './pages/public/GiftCardCheck';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './utils/auth';
import { SocketProvider } from './utils/socket';

// Pages publiques
import PublicBooking from './pages/public/PublicBooking';
import RoomsPage from './pages/public/RoomsPage';
import RoomDetailPage from './pages/public/RoomDetailPage';
import LandingPage from './pages/public/LandingPage';
import TableOrderPage from './pages/public/TableOrderPage';
// Stock, TableOrdersPage, AdminHappyHours, AdminPaymentMethods,
// AdminPriceProfiles, AdminPricingPeriods : ne sont plus montés en route
// directe — ils vivent en onglets internes (Mode caisse / Tarification, cf.
// POSPage.jsx et AdminTarification.jsx) ; les anciennes routes redirigent.
// Pages staff/admin
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Impersonate from './pages/Impersonate';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import BookingsPage from './pages/BookingsPage';
import BookingDetail from './pages/BookingDetail';
import CashPage from './pages/CashPage';
import SchedulePage from './pages/SchedulePage';
import ReportsPage from './pages/ReportsPage';
import TimerPage from './pages/TimerPage';
import AdminRooms from './pages/admin/AdminRooms';
import AdminUsers from './pages/admin/AdminUsers';
import AdminTeam from './pages/admin/AdminTeam';
import POSPage from './pages/POSPage';
import AdminSettings from './pages/admin/AdminSettings';
import AdminNotifications from './pages/admin/AdminNotifications';
import AdminSites from './pages/admin/AdminSites';
import AdminGiftCards from './pages/admin/AdminGiftCards';
import AdminRefunds from './pages/admin/AdminRefunds';
import { useWhiteLabel } from './utils/useWhiteLabel';
import AdminTarification from './pages/admin/AdminTarification';
import ModalHotkeys from './components/ModalHotkeys';
import AdminPermissions from './pages/admin/AdminPermissions';
import CustomersPage from './pages/CustomersPage';
import LivePage from './pages/LivePage';
import AdminLoyalty from './pages/admin/AdminLoyalty';
import AdminAuditLog from './pages/admin/AdminAuditLog';
import AdminPartners from './pages/admin/AdminPartners';

function PrivateRoute({ children, minRole }) {
  const { user, loading, can } = useAuth();
  if (loading) return <div className="loading-screen"><span className="spinner" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (minRole && !can(minRole)) return <Navigate to="/app" replace />;
  return children;
}

// Redirige vers /app si déjà connecté
function LoginRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><span className="spinner" /></div>;
  if (user) return <Navigate to="/app" replace />;
  return children;
}

export default function App() {
  useWhiteLabel();
  return (
    <AuthProvider>
      <SocketProvider>
        <BrowserRouter>
          <Toaster position="top-right" toastOptions={{ style: { background:'#1a1a2e', color:'#e8e8f0', border:'1px solid #2d2d4e' } }} />
          {/* Entrée = valide, Échap = annule, sur TOUTE modale de l'appli
              (générique, cf. components/ModalHotkeys.jsx) */}
          <ModalHotkeys />
          <Routes>
            {/* Landing page */}
            <Route path="/" element={<LandingPage />} />
            {/* Public */}
            <Route path="/reserver" element={<PublicBooking />} />
            <Route path="/reserver/:siteSlug" element={<PublicBooking />} />
            <Route path="/salles" element={<RoomsPage />} />
            <Route path="/salles/:slug" element={<RoomDetailPage />} />
            <Route path="/live/:roomId" element={<LivePage />} />
            <Route path="/bon-cadeau" element={<GiftCardCheck/>}/>
            <Route path="/commande/:qrToken" element={<TableOrderPage/>}/>
            <Route path="/superadmin" element={<SuperAdminApp/>}/>
            <Route path="/login" element={<LoginRoute><Login /></LoginRoute>} />
            <Route path="/mot-de-passe-oublie" element={<LoginRoute><ForgotPassword /></LoginRoute>} />
            <Route path="/reinitialiser-mot-de-passe" element={<LoginRoute><ResetPassword /></LoginRoute>} />
            <Route path="/impersonate" element={<Impersonate />} />

            {/* App staff/admin */}
            <Route path="/app" element={<PrivateRoute><Layout /></PrivateRoute>}>
              <Route index element={<Dashboard />} />
              <Route path="reservations" element={<BookingsPage />} />
              <Route path="reservations/:id" element={<BookingDetail />} />
              <Route path="caisse" element={<CashPage />} />
              <Route path="planning" element={<SchedulePage />} />
              {/* Stock, Happy Hours et Commandes à table sont maintenant des
                  onglets internes de Mode caisse — ces anciennes routes
                  redirigent pour ne pas casser les liens/favoris existants. */}
              <Route path="stock" element={<Navigate to="/app/caisse-pos?tab=stock" replace />} />
              <Route path="rapports" element={<ReportsPage />} />
              <Route path="timer" element={<TimerPage />} />
              <Route path="admin/salles" element={<PrivateRoute minRole="manager"><AdminRooms /></PrivateRoute>} />
              <Route path="caisse-pos" element={<PrivateRoute minRole="staff"><POSPage /></PrivateRoute>} />
              <Route path="table-orders" element={<Navigate to="/app/caisse-pos?tab=table-orders" replace />} />
              {/* Moyens de paiement, Profils tarifaires et Périodes tarifaires
                  sont regroupés en onglets internes de Tarification — mêmes
                  redirections de compatibilité. */}
              <Route path="admin/paiements" element={<Navigate to="/app/admin/tarification?tab=paiements" replace />} />
              <Route path="admin/tarification" element={<PrivateRoute minRole="manager"><AdminTarification /></PrivateRoute>} />
              <Route path="admin/equipe" element={<PrivateRoute minRole="admin"><AdminTeam /></PrivateRoute>} />
              <Route path="admin/utilisateurs" element={<PrivateRoute minRole="admin"><AdminUsers /></PrivateRoute>} />
              <Route path="admin/parametres" element={<PrivateRoute minRole="admin"><AdminSettings /></PrivateRoute>} />
              <Route path="clients" element={<CustomersPage />} />
              <Route path="admin/periodes" element={<Navigate to="/app/admin/tarification?tab=periodes" replace />} />
              <Route path="admin/gift-cards" element={<AdminGiftCards/>}/>
              <Route path="admin/remboursements" element={<AdminRefunds/>}/>
              <Route path="admin/sites" element={<PrivateRoute minRole="admin"><AdminSites /></PrivateRoute>} />
              <Route path="admin/happy-hours" element={<Navigate to="/app/caisse-pos?tab=happy" replace />} />
              <Route path="admin/tarifs" element={<Navigate to="/app/admin/tarification?tab=profils" replace />} />
              <Route path="admin/droits" element={<PrivateRoute minRole="admin"><AdminPermissions /></PrivateRoute>} />
              <Route path="admin/notifications" element={<PrivateRoute minRole="manager"><AdminNotifications /></PrivateRoute>} />
              <Route path="admin/fidelite" element={<PrivateRoute minRole="admin"><AdminLoyalty /></PrivateRoute>} />
              <Route path="admin/audit" element={<PrivateRoute minRole="admin"><AdminAuditLog /></PrivateRoute>} />
              <Route path="admin/partenaires" element={<PrivateRoute minRole="manager"><AdminPartners /></PrivateRoute>} />
            </Route>
          </Routes>
        </BrowserRouter>
      </SocketProvider>
    </AuthProvider>
  );
}
